import React from "react";
import { MessageInterface, ApiHelper, PendingCommand, TemplateInterface, UserHelper, DateHelper, ArrayHelper, ToastHelper } from "../../helpers";
import { Row, Col, FormControl, FormGroup, FormLabel, FormCheck } from "react-bootstrap"
import { InputBox, Loading } from "../../components"
import { Link, Navigate } from "react-router-dom";
import { MessageAnswers } from ".";
import ReactSelect from 'react-select'
import { Dots } from "react-activity"

interface Props {
  message: MessageInterface,
  template: TemplateInterface,
  refreshSidebar: () => void,
  refreshPage: () => void,
  pendingCommand: PendingCommand,
  sendCommand: (cmd: PendingCommand) => void,
  isLoading: boolean,
}

type OptionType = {
  value: string;
  label: string;
};

export const MessageEdit: React.FC<Props> = props => {
  const [message, setMessage] = React.useState<MessageInterface>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [redirect, setRedirect] = React.useState("");
  const [answersCommand, setAnswersCommand] = React.useState<PendingCommand>(null);
  const [submitProgress, setSubmitProgress] = React.useState(0);
  const [categoryInput, setCategoryInput] = React.useState("");

  const [categoryOptions, setCategoryOptions] = React.useState<OptionType[]>([{ value: "Test", label: "Test" }]);

  const handleCategoryChange = (newValue: { label: string, value: string }, obj: any) => {
    let m: MessageInterface = { ...message };
    m.category = newValue.value;
    setCategoryInput("");
    setMessage(m);
  }

  const handleCategoryBlur = () => {
    if (categoryInput) {
      const options = [...categoryOptions]
      options.push({ value: categoryInput, label: categoryInput })
      let m: MessageInterface = { ...message };
      m.category = categoryInput;
      setCategoryOptions(options);
      setMessage(m);
    }
  }

  const handleChange = (e: any) => {
    let m: MessageInterface = { ...message };
    let val = e.currentTarget.value;
    switch (e.currentTarget.id) {
      case "name": m.name = val; break;
      case "category": m.category = val; break;
      case "seconds": m.seconds = parseInt(val, 0); break;
      case "crossDepartment":
        const chk: any = document.getElementById('crossDepartment');
        m.crossDepartment = chk.checked;
        break;
      case "isPublic":
        const publicCheck: any = document.getElementById('isPublic');
        m.isPublic = publicCheck.checked;
        break;
    }
    setMessage(m);
  }

  const validate = () => {
    let errors: string[] = [];
    if (message.name === "") errors.push("Please enter a message name.");
    if (message.seconds < 5) errors.push("Messages must be displayed for at least 5 seconds");

    if (errors.length > 0) {
      errors.forEach(error => ToastHelper.error(error));
      return false;
    }
    return true;
  }

  const handleSave = () => {
    if (validate()) {
      setIsSubmitting(true);
      message.name = message.name.trim();
      message.category = message.category.trim();
      ApiHelper.post<MessageInterface[]>("/messages", [message])
        .then((data: MessageInterface[]) => {
          setAnswersCommand({ action: "save", parameter: data[0].id.toString() });
        })
        .catch((error) => {
          console.error("Error saving message:", error);
          ToastHelper.error("Failed to save message. Please try again.");
          setIsSubmitting(false);
        });
    }
  }

  const getDeleteFunction = () => (message?.id > 0) ? handleDelete : undefined

  const handleDelete = () => {
    if (window.confirm("Are you sure you wish to permanently delete this message?")) {
      ApiHelper.delete("/messages/" + message.id).then(() => {
        props.refreshSidebar();
        setRedirect("/messages");
      });
    }
  }

  /*
  const messagesSaved = () => {
    setIsSubmitting(false);
    props.refreshSidebar();
    setSavedKey(NavigationHelper.getRefreshKey());
  }
*/

  const answersSaved = (messageId: number) => {
    setIsSubmitting(false);
    props.refreshSidebar();
    ToastHelper.success("Changes saved successfully");
    if (window.location.href.indexOf("/messages/0?") > -1) {
      setRedirect("/messages/" + messageId + "?shape=" + message.shape);
    } else if ([3, 11, 14, 15].indexOf(message.templateId) > -1) {
      setTimeout(() => {
        props.refreshPage();
      }, 500)
    }
  }


  const handleAnswersCommand = (cmd: PendingCommand) => {
    if (cmd.action === "updateProgress") setSubmitProgress(cmd.parameter as number);
    else props.sendCommand(cmd); //updatePreview
  }


  const getAnswers = () => {
    return (
      <>
        {props.isLoading && (
          <div style={{ padding: '10px', textAlign: 'center', background: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6', marginBottom: '10px' }}>
            <Dots speed={0.4} animating={true} size={12} />
            <span style={{ marginLeft: '10px', fontSize: '12px', color: '#6c757d' }}>Loading template fields...</span>
          </div>
        )}
        <MessageAnswers message={message} pendingCommand={answersCommand} saveCallback={answersSaved} sendCommand={handleAnswersCommand} />
      </>
    );
  }


  const handleParentCommand = () => {
    if (props.pendingCommand) {
      if (props.pendingCommand.action === "updateAnswer" || props.pendingCommand.action === "updateArrayAnswer") setAnswersCommand(props.pendingCommand);
    }
  }

  const getHeader = () => {
    let items: React.ReactElement[] = [];

    if (message?.lastModified) items.push(<span style={{ fontStyle: "italic", fontWeight: "lighter", fontSize: 12 }}>Last updated: {DateHelper.getDisplayDuration(DateHelper.convertToDate(message.lastModified))}</span>)

    if (message.templateId === 7 && message.id > 0) {
      let copyLink = "/messages/0?templateId=7&copyFrom=" + message.id
      if (message?.shape === "9:16") copyLink += "&shape=" + message.shape;
      items.push(<Link to={copyLink} className="btn btn-info btn-sm">Copy</Link>);
    }
    return items;
  }

  const getChecks = () => {
    const result: React.ReactElement[] = [];
    if (UserHelper.checkAccess("admin")) {
      result.push(
        <FormGroup key="publicCheck" className="mb-3">
          <FormCheck data-testid="message-public-checkbox" id="isPublic" type="checkbox" label="Make Public" onChange={handleChange} checked={message?.isPublic} />
        </FormGroup>
      );
    }
    if (UserHelper.departments.length > 1) {
      result.push(
        <FormGroup key="crossDepartmentCheck" className="mb-3" controlId="formBasicCheckbox">
          <FormCheck data-testid="message-cross-department-checkbox" id="crossDepartment" type="checkbox" label="Show in all departments" onChange={handleChange} checked={message?.crossDepartment} />
        </FormGroup>
      );
    }
    return result;
  }


  React.useEffect(handleParentCommand, [props.pendingCommand]);
  React.useEffect(() => {
    setMessage(props.message);
    setIsSubmitting(false);
    if (redirect) setRedirect("");

    if (!props.message) return;

    ApiHelper.get<MessageInterface[]>("/messages").then((data) => {
      const cats = ArrayHelper.getStrings(data || [], "category");
      let options: OptionType[] = [];
      cats.forEach(c => {
        if (c) options.push({ value: c, label: c })
      });

      setCategoryOptions(options);
      if (props.message && !props.message.category && options.length > 0) {
        let m: MessageInterface = { ...props.message };
        m.category = options[0].value
        setMessage(m);
      }
/*
      let m: MessageInterface = { ...message };
      if (!m.id && options.length>0) {
        m.category = options[0].value
        setMessage(m);
      }
*/
      
    });
  }, [props.message]);  //eslint-disable-line


  if (redirect !== "") return <Navigate to={redirect} replace />
  else if (!message) return <Loading />
  else {
    let saveText = "Save";
    if (isSubmitting) saveText = "Saving...";
    // Keep button enabled but show loading state for dynamic fields
    const isSaveDisabled = isSubmitting || props.isLoading;

    const categoryOption = (message?.category === "" && categoryOptions.length > 0) ? categoryOptions[0] : { value: message.category, label: message.category }


    return (
      <InputBox headerIcon="fas fa-list-ul" headerText="Edit Message" saveFunction={handleSave} saveText={saveText} isSubmitting={isSaveDisabled} submitProgress={submitProgress} deleteFunction={getDeleteFunction()} headerActionContent={getHeader()} >
        {getChecks()}
        <Row>
          <Col xs={12} md={6}>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="template">Template</FormLabel>
              <FormControl data-testid="message-template-input" id="template" value={props.template?.name || ""} disabled={true} />
            </FormGroup>
          </Col>
          <Col xs={12} md={6}>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="seconds">Duration (in seconds)</FormLabel>
              <FormControl data-testid="message-duration-input" id="seconds" value={(props.template?.id === 11) ? "Determined by video" : message.seconds || ""} disabled={props.template?.id === 11} onChange={handleChange} />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="category">Category <span style={{fontSize:10}}>(Type in value to create a new category)</span></FormLabel>
              <ReactSelect data-testid="message-category-select" onInputChange={(newValue: string) => { setCategoryInput(newValue) }}
                value={categoryOption}
                onChange={handleCategoryChange}
                options={categoryOptions}
                onBlur={handleCategoryBlur}
              />
            </FormGroup>

          </Col>
          <Col xs={12} md={6}>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormControl data-testid="message-name-input" id="name" value={message.name} onChange={handleChange} />
            </FormGroup>
          </Col>
        </Row>
        {getAnswers()}
      </InputBox >
    );
  }
}
